/* Wish Karo - collection page helpers (loaded from sections/main-collection-banner.liquid)
   1. "Shop by age" row: edge fades, arrows for mouse users, selected age scrolled into view.
   2. Phones: the Filter button shows how many filters are on, the Sort button shows the current sort.
   3. Phone sort sheet: sorting keeps the filters that are on; Esc closes it.
   4. Phone filter drawer: choices are collected while it is open and applied once when it closes
      (global.js reloads the page on every URL change, which used to close the drawer after each tap). */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  const initAges = () => {
    document.querySelectorAll('.wk-ages').forEach((wrap) => {
      const list = wrap.querySelector('.wk-ages__list');
      if (!list) return;
      const nav = wrap.querySelector('.wk-ages__nav');
      const [prev, next] = nav ? nav.querySelectorAll('.wk-ages__arrow') : [];

      const update = () => {
        const max = list.scrollWidth - list.clientWidth;
        const overflow = max > 2;
        const atStart = list.scrollLeft <= 2;
        const atEnd = list.scrollLeft >= max - 2;
        list.classList.toggle('can-left', overflow && !atStart);
        list.classList.toggle('can-right', overflow && !atEnd);
        if (nav) {
          nav.hidden = !(overflow && finePointer.matches);
          if (prev) prev.disabled = atStart;
          if (next) next.disabled = atEnd;
        }
      };

      if (nav) {
        nav.addEventListener('click', (event) => {
          const button = event.target.closest('.wk-ages__arrow');
          if (!button || button.disabled) return;
          const dir = Number(button.dataset.wkDir) || 1;
          list.scrollBy({
            left: dir * Math.max(list.clientWidth * 0.75, 160),
            behavior: reduceMotion.matches ? 'auto' : 'smooth',
          });
        });
      }

      /* a selected age that sits past the visible part is brought into view, with the previous chip peeking */
      const active = list.querySelector('.wk-ages__chip.is-active');
      if (active) {
        const box = list.getBoundingClientRect();
        const chip = active.getBoundingClientRect();
        if (chip.right > box.right - 56 || chip.left < box.left) {
          list.scrollLeft += chip.left - box.left - 56;
        }
      }

      list.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      if (finePointer.addEventListener) finePointer.addEventListener('change', update);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(update);
      update();
    });
  };

  const initToolbar = () => {
    const bar = document.querySelector('#main-collection-filters .mobile-toolbar');
    if (!bar) return;
    const [filterBtn, sortBtn] = bar.querySelectorAll('.mobile-toolbar__btn');

    /* active filters = values in the URL; a price range counts once */
    const params = new URLSearchParams(location.search);
    let count = 0;
    let price = false;
    params.forEach((value, key) => {
      if (!key.startsWith('filter.') || value === '') return;
      if (key.startsWith('filter.v.price')) price = true;
      else count += 1;
    });
    if (price) count += 1;

    if (filterBtn && count > 0) {
      const badge = document.createElement('span');
      badge.className = 'wk-tool-badge';
      badge.textContent = count;
      badge.setAttribute('aria-hidden', 'true');
      filterBtn.appendChild(badge);
      filterBtn.classList.add('has-active');
      filterBtn.setAttribute('aria-label', `Open filters, ${count} on`);
    }

    if (sortBtn) {
      const current = document.querySelector('#MobileSortDrawer button[aria-current="true"]');
      const label = current && current.textContent.trim();
      if (label && label !== 'Featured') {
        const span = sortBtn.querySelector('span:last-child');
        if (span) span.textContent = label.replace('Price: ', '');
        sortBtn.classList.add('has-active');
        sortBtn.setAttribute('aria-label', `Sort, now ${label}`);
      }
    }
  };

  const initSortSheet = () => {
    const sheet = document.getElementById('MobileSortDrawer');
    if (!sheet) return;
    const panel = sheet.querySelector('.mobile-sort-drawer__panel');
    const form = sheet.querySelector('form');
    const opener = document.querySelector('#main-collection-filters .mobile-toolbar__btn + .mobile-toolbar__btn');

    if (panel) {
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
      panel.setAttribute('aria-label', 'Sort by');
    }

    /* the sheet's own form only sends sort_by, which dropped every filter that was on */
    if (form) {
      form.addEventListener('click', (event) => {
        const button = event.target.closest('button[name="sort_by"]');
        if (!button) return;
        event.preventDefault();
        const url = new URL(location.href);
        url.searchParams.set('sort_by', button.value);
        url.searchParams.delete('page');
        location.assign(url.toString());
      });
    }

    let wasOpen = false;
    new MutationObserver(() => {
      const open = sheet.classList.contains('open');
      if (open === wasOpen) return;
      wasOpen = open;
      if (open) {
        const target = sheet.querySelector('button[aria-current="true"]') || sheet.querySelector('button[name="sort_by"]');
        if (target) target.focus({ preventScroll: true });
      } else if (opener && (sheet.contains(document.activeElement) || document.activeElement === document.body)) {
        opener.focus({ preventScroll: true });
      }
    }).observe(sheet, { attributes: true, attributeFilter: ['class'] });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && sheet.classList.contains('open')) sheet.classList.remove('open');
    });
  };

  const initFilterDrawer = () => {
    const form = document.getElementById('FacetFiltersFormMobile');
    const drawer = form && form.closest('details');
    if (!form || !drawer) return;

    /* the drawer only filters (sorting has its own sheet) */
    const heading = form.querySelector('.mobile-facets__heading');
    if (heading && !form.querySelector('[name="sort_by"]')) heading.textContent = 'Filter';

    const filterKey = (pairs) =>
      pairs
        .filter(([key, value]) => key.startsWith('filter.') && value !== '')
        .map(([key, value]) => `${key}=${value}`)
        .sort()
        .join('&');

    const targetUrl = () => {
      const next = new URLSearchParams();
      for (const [key, value] of new FormData(form)) {
        if (key.startsWith('filter.') && value !== '') next.append(key, value);
      }
      /* keep sort order and anything else that is not a filter; start again from the first page */
      for (const [key, value] of new URLSearchParams(location.search)) {
        if (!key.startsWith('filter.') && key !== 'page') next.append(key, value);
      }
      const query = next.toString();
      return location.pathname + (query ? `?${query}` : '');
    };

    let pending = false;
    const applyIfChanged = () => {
      if (!pending) return false;
      pending = false;
      const now = filterKey([...new URLSearchParams(location.search)]);
      const chosen = filterKey([...new FormData(form)]);
      if (now === chosen) return false;
      location.assign(targetUrl());
      return true;
    };

    /* hold each change here instead of letting facets.js submit it straight away */
    form.addEventListener(
      'input',
      (event) => {
        if (!drawer.open) return;
        event.stopPropagation();
        pending = true;
      },
      true
    );

    /* Apply buttons go now; any other way of closing the drawer applies on close */
    form.addEventListener(
      'click',
      (event) => {
        const apply = event.target.closest('.mobile-facets__footer .button');
        if (apply && applyIfChanged()) event.preventDefault();
      },
      true
    );

    new MutationObserver(() => {
      if (!drawer.open) applyIfChanged();
    }).observe(drawer, { attributes: true, attributeFilter: ['open'] });
  };

  const init = () => {
    initAges();
    initToolbar();
    initSortSheet();
    initFilterDrawer();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();